import React, {useCallback, useMemo, useState} from "react";
import {Box, Card, CardContent, CardMedia, Typography} from "@mui/material";
import ReactECharts from "echarts-for-react";
import {useSelector} from "react-redux";
import PageLayout from "../components/layout/PageLayout";
import ReportFilters from "../components/reports/ReportFilters";
import {selectCategories} from "../store/categorySlice";
import lineChartPreview from "../assets/reports/income-vs-expense.svg";
import {useTranslation} from "react-i18next";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const pad2 = (value) => String(value).padStart(2, "0");
const formatDate = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseDate = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const getMonthEnd = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const expandSinglePeriod = (period) => {
    const fromDate = parseDate(period?.dateFrom);
    const toDate = parseDate(period?.dateTo);
    if (!fromDate || !toDate) {
        return {periods: [], aggregation: "DAY"};
    }
    let start = fromDate;
    let end = toDate;
    if (start > end) {
        [start, end] = [end, start];
    }
    const daysCount = Math.floor((end - start) / MS_PER_DAY) + 1;
    if (daysCount <= 100) {
        const periods = [];
        for (let cursor = new Date(start); cursor <= end; cursor = addDays(cursor, 1)) {
            const date = formatDate(cursor);
            periods.push({dateFrom: date, dateTo: date});
        }
        return {periods, aggregation: "DAY"};
    }
    const periods = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
        const monthStart = cursor < start ? start : cursor;
        const monthEnd = getMonthEnd(cursor);
        const cappedEnd = monthEnd > end ? end : monthEnd;
        periods.push({dateFrom: formatDate(monthStart), dateTo: formatDate(cappedEnd)});
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return {periods, aggregation: "MONTH"};
};

const formatLabel = (period, aggregation, fallback) => {
    if (!period?.dateFrom) return fallback;
    const date = parseDate(period.dateFrom);
    if (!date) return fallback;
    if (aggregation === "MONTH") {
        return new Intl.DateTimeFormat("en-GB", {month: "short", year: "numeric"}).format(date);
    }
    return new Intl.DateTimeFormat("en-GB", {day: "numeric", month: "short"}).format(date);
};

const ReportsIncomeVsExpenseLineChartPage = () => {
    const {t} = useTranslation();
    const categories = useSelector(selectCategories);
    const [reportResponses, setReportResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const formatPeriodRange = useCallback((period) => {
        const fromDate = parseDate(period?.dateFrom);
        const toDate = parseDate(period?.dateTo);
        if (!fromDate || !toDate) {
            return t("common.labels.period");
        }
        const formatter = new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
        const fromLabel = formatter.format(fromDate);
        const toLabel = formatter.format(toDate);
        return `${fromLabel} - ${toLabel}`;
    }, []);

    const categoryTypeById = useMemo(() => {
        return new Map((categories || []).map((category) => [category.id, category.type]));
    }, [categories]);

    const handleApply = useCallback(async (request) => {
        const rawPeriods = request?.periods || [];
        if (!rawPeriods.length) {
            setLoadError(t("reports.messages.selectPeriodRange"));
            setReportResponses([]);
            return;
        }

        setIsLoading(true);
        setLoadError(null);
        try {
            const expandedSets = rawPeriods.map((period, index) => {
                const {periods, aggregation} = expandSinglePeriod(period);
                const bucketLabels = periods.map((entry, entryIndex) =>
                    formatLabel(entry, aggregation, `Period ${index + 1}-${entryIndex + 1}`)
                );
                return {
                    originalPeriod: period,
                    aggregation,
                    periods,
                    bucketLabels
                };
            });

            const responseList = await Promise.all(
                expandedSets.map(async (expanded) => {
                    if (!expanded.periods.length) {
                        return {expanded, data: null};
                    }
                    const response = await fetch("/api/reports/matrix", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            ...request,
                            periods: expanded.periods
                        })
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to load report (${response.status})`);
                    }
                    const json = await response.json();
                    return {expanded, data: json};
                })
            );

            const prepared = responseList
                .filter((item) => item.expanded.periods.length)
                .map((item, index) => ({
                    key: `period-${index}`,
                    label: formatPeriodRange(item.expanded.originalPeriod),
                    aggregation: item.expanded.aggregation,
                    bucketLabels: item.expanded.bucketLabels,
                    data: item.data
                }));

            setReportResponses(prepared);
        } catch (error) {
            console.log(error);
            setLoadError(error?.message || t("reports.messages.failedToLoadReport"));
            setReportResponses([]);
        } finally {
            setIsLoading(false);
        }
    }, [formatPeriodRange]);

    const handleReset = useCallback(() => {
        setReportResponses([]);
        setLoadError(null);
    }, []);

    const chartOption = useMemo(() => {
        if (!reportResponses.length) {
            return null;
        }
        const longestLabels = reportResponses.reduce((acc, current) => {
            return current.bucketLabels.length > acc.length ? current.bucketLabels : acc;
        }, []);
        const maxBuckets = longestLabels.length;
        if (!maxBuckets) {
            return null;
        }
        const labels = longestLabels;
        const series = [];

        reportResponses.forEach((response) => {
            const rows = response.data?.rows || [];
            const periods = response.data?.periods || [];
            const totals = periods.map(() => ({expense: 0, income: 0}));
            rows.forEach((row) => {
                const type = categoryTypeById.get(row.categoryId);
                if (!type) {
                    return;
                }
                (row.values || []).forEach((value, index) => {
                    const amount = Math.abs(Number(value || 0));
                    if (type === "EXPENSE") {
                        totals[index].expense += amount;
                    } else if (type === "INCOME") {
                        totals[index].income += amount;
                    }
                });
            });

            const expenseData = totals.map((item) => item.expense);
            const incomeData = totals.map((item) => item.income);
            while (expenseData.length < maxBuckets) {
                expenseData.push(null);
                incomeData.push(null);
            }
            series.push(
                {
                    name: `${t("common.transactionType.expense")} (${response.label})`,
                    type: "line",
                    smooth: true,
                    data: expenseData
                },
                {
                    name: `${t("common.transactionType.income")} (${response.label})`,
                    type: "line",
                    smooth: true,
                    data: incomeData
                }
            );
        });

        return {
            tooltip: {
                trigger: "axis"
            },
            legend: {
                bottom: 0
            },
            grid: {
                left: 40,
                right: 20,
                top: 40,
                bottom: 80
            },
            xAxis: {
                type: "category",
                data: labels
            },
            yAxis: {
                type: "value"
            },
            series
        };
    }, [categoryTypeById, reportResponses]);

    return (
        <PageLayout title={t("pages.reports.incomeVsExpense.lineChartTitle")}>
            <CardContent sx={{minHeight: 200, maxHeight: "90%", overflowY: "auto"}}>
                <ReportFilters
                    showTypeTabs={false}
                    showCategories={false}
                    includeReportTypeFilter={false}
                    onApply={handleApply}
                    onReset={handleReset}
                />
                {isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loading")}
                    </Typography>
                )}
                {loadError && (
                    <Typography variant="body2" color="error" sx={{mb: 2}}>
                        {loadError}
                    </Typography>
                )}
                <Card sx={{maxWidth: 1100, margin: "0 auto"}}>
                    {chartOption ? (
                        <Box sx={{height: 520, width: "100%"}}>
                            <ReactECharts option={chartOption} style={{height: "100%", width: "100%"}} />
                        </Box>
                    ) : (
                        <CardMedia
                            component="img"
                            image={lineChartPreview}
                            alt={t("pages.reports.incomeVsExpense.cards.line.alt")}
                        />
                    )}
                </Card>
            </CardContent>
        </PageLayout>
    );
};

export default ReportsIncomeVsExpenseLineChartPage;
